import type * as React from "react";

import classNames from "classnames";
import { CloseIcon } from "components/ui/Icons/CloseIcon";
import { TooltipOnOverflow } from "components/ui/TooltipOnOverflow";
import { type TTypographyVariant, Typography } from "components/ui/Typography";
import { Map } from "immutable";
import { useCallback, useMemo } from "react";

import { useStyles } from "./styles";

export type TChipSize = "large" | "medium" | "small";
export type TChipVariant = "accent" | "danger" | "light" | "outlined" | "primary" | "regular" | "success";

const REGULAR_TEXT_VARIANT_MAP = Map<TChipSize, TTypographyVariant>({
  large: "text_reg",
  medium: "text_sm_reg",
  small: "text_tny_reg",
});

const SEMIBOLD_TEXT_VARIANT_MAP = Map<TChipSize, TTypographyVariant>({
  large: "text_title_sb",
  medium: "text_sm_sb",
  small: "text_tny_sb",
});

const SMALL_ICON_SIZE = 12;
const ICON_SIZE = 16;
export interface IChipProps {
  disabled?: boolean;
  inactive?: boolean;
  noShrink?: boolean;
  noTooltip?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onDelete?: () => void;
  onSuffixIconClick?: (e: React.MouseEvent) => void;
  PrefixIcon?: IconComponent;
  readonly?: boolean;
  selected?: boolean;
  size?: TChipSize;
  SuffixIcon?: IconComponent;
  variant?: TChipVariant;
}

/** @deprecated Use the new `Badge` component from `components/ui/badge`. */
export const Chip: FC<IChipProps & TWithTestId> = ({
  children,
  className,
  "data-testid": dataTestId,
  disabled = false,
  inactive = false,
  noShrink = false,
  noTooltip = false,
  onClick: onChipClick,
  onDelete,
  onSuffixIconClick,
  PrefixIcon,
  readonly = false,
  ref,
  selected = false,
  size = "medium",
  SuffixIcon,
  variant = "regular",
  ...restProps
}) => {
  const classes = useStyles();
  const { iconSize, sizeClass } = useMemo(() => {
    switch (size) {
      case "large":
        return { iconSize: ICON_SIZE, sizeClass: classes.large };
      case "small":
        return { iconSize: SMALL_ICON_SIZE, sizeClass: classes.small };
      case "medium":
      default:
        return { iconSize: ICON_SIZE, sizeClass: classes.medium };
    }
  }, [classes.large, classes.medium, classes.small, size]);

  const { colorClass, iconColorClass } = useMemo(() => {
    switch (variant) {
      case "accent":
        return { colorClass: classes.accent, iconColorClass: classes.accentIcon };
      case "danger":
        return { colorClass: classes.danger, iconColorClass: classes.dangerIcon };
      case "light":
        return { colorClass: classes.light, iconColorClass: classes.lightIcon };
      case "outlined":
        return { colorClass: classes.outlined, iconColorClass: classes.regularIcon };
      case "primary":
        return { colorClass: classes.primary, iconColorClass: classes.primaryIcon };
      case "success":
        return { colorClass: classes.success, iconColorClass: classes.successIcon };
      case "regular":
      default:
        return { colorClass: classes.regularColor, iconColorClass: classes.regularIcon };
    }
  }, [classes, variant]);

  const getSuffix = useCallback(() => {
    if (onDelete) {
      return (
        <CloseIcon
          className={classNames(iconColorClass, classes.clickable)}
          clickable={false}
          data-testid={dataTestId && `${dataTestId}-delete`}
          disabled={disabled}
          onClick={onDelete}
          size={iconSize}
        />
      );
    } else if (SuffixIcon) {
      return (
        <SuffixIcon
          className={iconColorClass}
          clickable={false}
          disabled={disabled}
          onClick={onSuffixIconClick}
          size={iconSize}
        />
      );
    }
    return null;
  }, [onDelete, dataTestId, iconColorClass, disabled, iconSize, SuffixIcon, onSuffixIconClick, classes.clickable]);

  const suffix = getSuffix();
  return (
    <div
      data-interrupt-parent-tooltip // this is used to interrupt chip tooltip on PrefixIcon tooltip
      data-testid={dataTestId}
      ref={ref}
      {...restProps}
      className={classNames(
        classes.chip,
        colorClass,
        sizeClass,
        {
          [classes.chipWithOneIcon]: (PrefixIcon || suffix) && !(PrefixIcon && suffix),
          [classes.chipWithTwoIcons]: PrefixIcon && suffix,
          [classes.clickable]: !!onChipClick && !disabled,
          [classes.disabled]: disabled,
          [classes.inactive]: inactive,
          [classes.noContent]: !children,
          [classes.noShrink]: noShrink,
          [classes.readOnly]: readonly,
          [classes.selected]: selected,
        },
        className,
      )}
      onClick={!disabled ? onChipClick : undefined}
    >
      {PrefixIcon && <PrefixIcon size={iconSize} />}

      {children
        ? (
            !noTooltip
              ? (
                  <TooltipOnOverflow
                    className={classes.chipText}
                    content={children}
                    textVariant={selected ? SEMIBOLD_TEXT_VARIANT_MAP.get(size) : REGULAR_TEXT_VARIANT_MAP.get(size)}
                  />
                )
              : (
                  <Typography
                    className={classes.chipText}
                    noWrap={true}
                    variant={selected ? SEMIBOLD_TEXT_VARIANT_MAP.get(size) : REGULAR_TEXT_VARIANT_MAP.get(size)}
                  >
                    {children}
                  </Typography>
                )
          )
        : null}

      {suffix}
    </div>
  );
};
